import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import moment from 'moment';

import { LibraryIcon } from '@treams/treams-icons';
import { Redirect, Route, Switch } from '@treams/treams-routes';
import { Subheading, Sidebar, SidebarLayout, Skeleton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import EngagementCategory from './EngagementCategory';
import CreateEngagementCategory from './CreateEngagementCategory';
import CreateEngagementTemplate from './CreateEngagementTemplate';
import EngagementSurveySettings from './surveys/EngagementSurveySettings';

type RouteParams = {
	id: string;
};

export default function FeedbackSettings() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = graphql.useEngagementSettingsQuery();

	const [optimistic, setOptimistic] = useState<graphql.EngagementCategory[]>([]);
	const [optimisticTemplates, setOptimisticTemplates] = useState<graphql.EngagementTemplate[]>([]);

	const builtin = _.sortBy(
		query.data?.builtinEngagementCategories.edges.map(({ node }) => node),
		(category) => localize(category.title).text,
	);

	const all = _.sortBy(
		_.uniqBy(
			query.data?.engagementCategories.edges.map(({ node }) => node).concat(optimistic),
			'id',
		),
		(category) => localize(category.title).text,
	);

	const [archived, categories] = _.partition(all, (category) => category.isArchived);

	const templates = _.sortBy(
		_.uniqBy(
			query.data?.engagementTemplates.edges.map(({ node }) => node).concat(optimisticTemplates),
			'id',
		),
		(template) => localize(template.title).text,
	);

	return (
		<SidebarLayout
			sidebar={
				<Sidebar>
					{query.data ? (
						<>
							<Subheading mb={2}> {t('settings.engagementLibrary.surveys.title')}</Subheading>
							{_.sortBy(templates).map((node) => (
								<Sidebar.Item
									key={node.id}
									title={localize(node.title).text}
									subtitle={
										node.nextRequestAt
											? t('settings.engagementLibrary.surveys.nextRequestAt', {
													date: moment(node.nextRequestAt).format('LL'),
											  })
											: t('settings.engagementLibrary.surveys.nextRequestNotScheduled')
									}
									small
									exact
									path={`/settings/engagement/surveys/${node.id}`}
									status={node.isEnabled ? 'active' : 'inactive'}
								/>
							))}
							<Box mt={3}>
								<CreateEngagementTemplate
									onCreate={(template) =>
										setOptimisticTemplates((optimistic) => [...optimistic, template])
									}
								/>
							</Box>
							<Sidebar.Separator />
							<Subheading mb={2}> {t('settings.engagementLibrary.lists.title')}</Subheading>
							{builtin.map((node) => (
								<Sidebar.Item
									key={node.id}
									icon={<LibraryIcon />}
									title={localize(node.title).text}
									small
									exact
									path={`/settings/engagement/categories/${node.id}`}
								/>
							))}

							{categories.length > 0 && (
								<>
									<Sidebar.Separator />
									<Subheading mb={2}>
										{t('settings.engagementLibrary.lists.custom.title')}
									</Subheading>
									{categories.map((node) => (
										<Sidebar.Item
											key={node.id}
											title={localize(node.title).text}
											small
											exact
											path={`/settings/engagement/categories/${node.id}`}
										/>
									))}
								</>
							)}

							<Box mt={3}>
								<CreateEngagementCategory
									onCreate={(category) => setOptimistic([...optimistic, category])}
								/>
							</Box>

							{archived.length > 0 && (
								<>
									<Sidebar.Separator />
									<Subheading mb={2}>{t('settings.engagementLibrary.lists.archived')}</Subheading>
									{archived.map((category) => (
										<Sidebar.Item
											key={category.id}
											title={localize(category.title).text}
											small
											exact
											path={`/settings/engagement/categories/${category.id}`}
										/>
									))}
								</>
							)}
						</>
					) : (
						<>
							<Subheading mb={2}> {t('settings.engagementLibrary.surveys.title')}</Subheading>
							<Skeleton.Repeat count={3}>
								<Sidebar.Item.Skeleton small />
							</Skeleton.Repeat>
							<Sidebar.Separator />
							<Subheading mb={2}> {t('settings.engagementLibrary.lists.title')}</Subheading>
							<Skeleton.Repeat count={3}>
								<Sidebar.Item.Skeleton small />
							</Skeleton.Repeat>
						</>
					)}
				</Sidebar>
			}
		>
			<Switch>
				<Route path="/settings/engagement" exact>
					{templates.length > 0 && (
						<Redirect to={`/settings/engagement/surveys/${templates[0].id}`} />
					)}
				</Route>
				<Route<RouteParams> path="/settings/engagement/surveys/:id" exact>
					{({ match }) => <EngagementSurveySettings id={match!.params.id!} />}
				</Route>
				<Route<RouteParams> path="/settings/engagement/categories/:id">
					{({ match }) => <EngagementCategory id={match?.params.id ?? ''} />}
				</Route>
			</Switch>
		</SidebarLayout>
	);
}
